import { AlertTriangle, RefreshCw } from 'lucide-react'
import type { ExitPipelineWeek, OpsBoard, RbsZone } from '@/lib/api'
import { useOpsBoard } from '@/hooks/useOps'
import { useSession } from '@/lib/session'
import { formatWadMultiple, formatWadPercent, formatWadUsd, fromWad, WAD } from '@/lib/wad'
import { Button } from '@/ui/Button'
import { CardBody, CardHeader, Housing } from '@/ui/Card'
import { Badge, Callout, ErrorState, Skeleton } from '@/ui/primitives'
import { Gauge, Readout, ReadoutRow, StatBlock, type Tone } from '@/ui/readout'

/** LCR_TARGET from the protocol constants — 1.5×. Drawn as the gauge's ceiling. */
const LCR_TARGET = (15n * WAD) / 10n
/** Below 20% of its high-water mark the RBS buy-side floor defense is spent. */
const RBS_DEPLETION_FLOOR = WAD / 5n

const ZONE_TONE: Record<RbsZone, Tone> = {
  BUY: 'fault',
  IDLE: 'fluid',
  MINT: 'brass',
}

const ZONE_MEANING: Record<RbsZone, string> = {
  BUY: 'Market price is under the lower band — the treasury is defending the floor by buying.',
  IDLE: 'Price is inside the band. The stabiliser is not acting.',
  MINT: 'Price is over the upper band — the treasury is minting and selling into the premium.',
}

/**
 * The operations board.
 *
 * One question, four ways of asking it: can the protocol pay everyone scheduled to
 * leave, and is it about to fall out of the band? Alerts first because they are the
 * server's own answer; the panels below are the evidence behind them, in the order an
 * operator checks it — solvency, then the stabiliser's ammunition, then the obligations
 * arriving over the next month.
 */
export function Ops() {
  const { isAdmin } = useSession()
  const { data, isLoading, isError, error, refetch, isFetching } = useOpsBoard(isAdmin)

  if (isLoading) return <OpsSkeleton />

  if (isError || !data) {
    return (
      <Housing>
        <ErrorState
          message={(error as Error | null)?.message ?? 'Could not load the operations board.'}
          retry={() => void refetch()}
        />
      </Housing>
    )
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="display display-tight text-3xl">Operations</h1>
          <p className="mt-1.5 text-sm text-muted">
            Block <span className="tnum">{data.snapshot.blockNumber.toString()}</span> · refreshes
            every 15s
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void refetch()}
          loading={isFetching}
          iconLeft={<RefreshCw className="size-3.5" aria-hidden />}
        >
          Refresh
        </Button>
      </header>

      <Alerts alerts={data.alerts} />

      {/*
       * Headline figures. Each carries the tone its MEANING demands, not the tone of
       * whether it is currently good: `backingPerCdx` is fluid because it is a stable
       * amount, `mintHeadroom` is brass because it is a ceiling. LCR is the exception —
       * it takes the server's own status, because "is this number bad" is exactly what
       * an LCR is for.
       */}
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <Housing className="p-5">
          <StatBlock
            label="Liquidity coverage"
            value={formatWadMultiple(data.snapshot.lcr)}
            size="sm"
            tone={
              data.snapshot.lcrStatus === 'critical'
                ? 'fault'
                : data.snapshot.lcrStatus === 'warning'
                  ? 'brass'
                  : 'fluid'
            }
            note={`Target ${formatWadMultiple(LCR_TARGET)}. Below 1.00× not every scheduled exit can clear.`}
          />
          <Gauge
            className="mt-4"
            // Scaled to 2× rather than to the 1.5× target: a target drawn at the very end
            // of the track leaves no room to show being comfortably above it, and healthy
            // is the state this gauge spends most of its life in.
            value={fromWad(data.snapshot.lcr) / 2}
            ceiling={fromWad(LCR_TARGET) / 2}
            ceilingLabel="Target"
            tone={data.snapshot.lcrStatus === 'healthy' ? 'fluid' : 'enamel'}
          />
        </Housing>

        <Housing className="p-5">
          <StatBlock
            label="Premium index"
            value={formatWadMultiple(data.snapshot.premiumIndex)}
            size="sm"
            tone="enamel"
            note="Market price over backing. 1.00× is parity."
          />
          <div className="mt-4">
            <Badge tone={data.snapshot.rbsZone === 'IDLE' ? 'positive' : 'warning'}>
              RBS {data.snapshot.rbsZone}
            </Badge>
            <p className="mt-2 text-xs leading-relaxed text-muted">
              {ZONE_MEANING[data.snapshot.rbsZone]}
            </p>
          </div>
        </Housing>

        <Housing className="p-5">
          <StatBlock
            label="RBS reserve"
            value={formatWadUsd(data.rbsReserve.usdt)}
            size="sm"
            tone={data.rbsReserve.depleted ? 'fault' : ZONE_TONE[data.snapshot.rbsZone]}
            note={`${formatWadPercent(data.rbsReserve.remainingFraction)} of the high-water mark remains.`}
          />
          <Gauge
            className="mt-4"
            value={fromWad(data.rbsReserve.remainingFraction)}
            ceiling={fromWad(RBS_DEPLETION_FLOOR)}
            ceilingLabel="Spent"
            tone={data.rbsReserve.depleted ? 'enamel' : 'fluid'}
          />
        </Housing>

        <Housing className="p-5">
          <StatBlock
            label="Settlement queue"
            value={data.staleQueueCount.toString()}
            size="sm"
            tone={data.staleQueueCount > 0 ? 'brass' : 'muted'}
            note="Turbine entries past their ready time and still unsettled."
          />
          <Readout className="mt-4">
            <ReadoutRow
              dense
              label="Mint headroom"
              value={formatWadUsd(data.snapshot.mintHeadroom)}
              tone="brass"
            />
          </Readout>
        </Housing>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_320px]">
        <ExitPipeline weeks={data.exitPipeline} />

        <Housing>
          <CardHeader title="Price" subtitle="What the two figures behind the index are." />
          <CardBody className="pt-4">
            <Readout>
              <ReadoutRow
                label="Market price"
                value={formatWadUsd(data.snapshot.marketPrice)}
                tone="enamel"
              />
              <ReadoutRow
                label="Backing per CDX"
                value={formatWadUsd(data.snapshot.backingPerCdx)}
                tone="fluid"
              />
              <ReadoutRow
                net
                label="Premium index"
                value={formatWadMultiple(data.snapshot.premiumIndex)}
              />
            </Readout>
            <p className="mt-4 text-xs leading-relaxed text-muted">
              Backing is what the treasury actually holds behind each token. A premium
              index far from parity in either direction is what moves the stabiliser out
              of IDLE.
            </p>
          </CardBody>
        </Housing>
      </div>
    </div>
  )
}

function Alerts({ alerts }: { alerts: OpsBoard['alerts'] }) {
  if (alerts.length === 0) {
    return (
      <Callout tone="info" title="No alerts">
        Coverage, reserve and settlement queue are all within their thresholds.
      </Callout>
    )
  }

  // Critical before warning. The server emits them in the order it computes them, which
  // is not the order an operator should read them in.
  const ordered = [...alerts].sort((a, b) =>
    a.level === b.level ? 0 : a.level === 'critical' ? -1 : 1,
  )

  return (
    <div className="space-y-3">
      {ordered.map((alert, i) => (
        <Callout
          key={`${alert.level}-${i}`}
          tone={alert.level === 'critical' ? 'danger' : 'warning'}
          title={alert.level}
        >
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {alert.message}
          </span>
        </Callout>
      ))}
    </div>
  )
}

/**
 * The four-week exit pipeline.
 *
 * Bucketed by week rather than totalled so a CLIFF is visible before it arrives instead
 * of on the day it lands — a month's obligations summed to one number hides the week
 * that cannot be paid. Bars are scaled to the largest week in the window, which makes
 * the shape of the month readable at a glance; the figures beside them are what to act
 * on.
 */
function ExitPipeline({ weeks }: { weeks: ExitPipelineWeek[] }) {
  const peak = weeks.reduce((max, w) => (w.scheduledExitUsdt > max ? w.scheduledExitUsdt : max), 0n)
  const total = weeks.reduce((sum, w) => sum + w.scheduledExitUsdt, 0n)

  return (
    <Housing>
      <CardHeader
        title="Exit pipeline"
        subtitle="What the protocol owes over the next four weeks, and the bonds maturing into it."
        action={
          <div className="text-right">
            <p className="label">30-day total</p>
            <p className="tnum mt-1 text-lg text-ink">{formatWadUsd(total)}</p>
          </div>
        }
      />
      <CardBody className="pt-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="label py-2 text-left">Week</th>
                <th className="label py-2 text-left">Scheduled exits</th>
                <th className="label py-2 text-right">Bonds maturing</th>
                <th className="label py-2 text-right">Principal</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week} className="border-b border-line last:border-0">
                  <td className="py-3 pr-4 align-middle">
                    <span className="text-ink">W{w.week}</span>
                    <span className="mt-0.5 block text-[10px] text-faint">
                      {formatRange(w.from, w.to)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 align-middle">
                    <span className="tnum text-ink">{formatWadUsd(w.scheduledExitUsdt)}</span>
                    <span
                      aria-hidden
                      className="mt-1.5 block h-1 rounded-full"
                      style={{
                        // Guard the zero case explicitly: an empty window is a real state,
                        // and dividing by a zero peak would render every bar as NaN%.
                        width:
                          peak === 0n
                            ? '0%'
                            : `${(fromWad(w.scheduledExitUsdt) / fromWad(peak)) * 100}%`,
                        minWidth: w.scheduledExitUsdt > 0n ? '2px' : undefined,
                        background: 'var(--m-gauge-enamel)',
                      }}
                    />
                  </td>
                  <td className="tnum py-3 pr-4 text-right align-middle text-muted">
                    {w.maturingBonds.toLocaleString()}
                  </td>
                  <td className="tnum py-3 text-right align-middle text-fluid-text">
                    {formatWadUsd(w.maturingPrincipal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Housing>
  )
}

/** "18 Aug – 25 Aug". Day and month only — the year is never in question here. */
function formatRange(from: string, to: string): string {
  const fmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })
  return `${fmt.format(new Date(from))} – ${fmt.format(new Date(to))}`
}

function OpsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-9 w-56" />
      <Skeleton className="h-16 w-full" />
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-44 w-full" />
        ))}
      </div>
      <Skeleton className="h-72 w-full" />
    </div>
  )
}
